import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Filter, Plus, X } from "lucide-react";
import { toast } from "sonner";

interface FilterCondition {
  id: string;
  field: string;
  operator: string;
  value: string;
}

interface FilterBuilderProps {
  fields: any[];
  onApplyFilters: (filters: FilterCondition[]) => void;
  currentFilters: FilterCondition[];
}

export const FilterBuilder = ({ fields, onApplyFilters, currentFilters }: FilterBuilderProps) => {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<FilterCondition[]>(currentFilters);

  const operators = [
    { value: "equals", label: "Equals" },
    { value: "contains", label: "Contains" },
    { value: "startsWith", label: "Starts with" },
    { value: "endsWith", label: "Ends with" },
    { value: "greaterThan", label: "Greater than" },
    { value: "lessThan", label: "Less than" },
    { value: "isEmpty", label: "Is empty" },
    { value: "isNotEmpty", label: "Is not empty" },
  ];

  const addFilter = () => {
    setFilters([
      ...filters,
      {
        id: Math.random().toString(),
        field: fields[0]?.name || "",
        operator: "equals",
        value: "",
      },
    ]);
  };

  const removeFilter = (id: string) => {
    setFilters(filters.filter((f) => f.id !== id));
  };

  const updateFilter = (id: string, key: keyof FilterCondition, value: string) => {
    setFilters(filters.map((f) => (f.id === id ? { ...f, [key]: value } : f)));
  };

  const handleApply = () => {
    onApplyFilters(filters);
    setOpen(false);
    toast.success("Filters applied");
  };

  const handleClear = () => {
    setFilters([]);
    onApplyFilters([]);
    toast.success("Filters cleared");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter {currentFilters.length > 0 && `(${currentFilters.length})`}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Filter Records</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {filters.map((filter) => (
            <div key={filter.id} className="flex gap-2 items-start">
              <Select
                value={filter.field}
                onValueChange={(value) => updateFilter(filter.id, "field", value)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((field) => (
                    <SelectItem key={field.id} value={field.name}>
                      {field.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filter.operator}
                onValueChange={(value) => updateFilter(filter.id, "operator", value)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!["isEmpty", "isNotEmpty"].includes(filter.operator) && (
                <Input
                  value={filter.value}
                  onChange={(e) => updateFilter(filter.id, "value", e.target.value)}
                  placeholder="Value"
                  className="flex-1"
                />
              )}
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeFilter(filter.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button onClick={addFilter} variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Condition
          </Button>
        </div>
        <div className="flex gap-2 justify-end pt-4">
          <Button variant="outline" onClick={handleClear}>
            Clear All
          </Button>
          <Button onClick={handleApply}>Apply Filters</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
